<template>
    <div class="sidebar-detail ui-fillout-height" v-if="sidebarDetailOpen">
        <component id="scroll" :is="nativeScrolling ? 'div' : 'PerfectScrollbar'" class="sidebar-detail__scrollbar" :class="{'sidebar__scrollbar--native': nativeScrolling}" ref="sidebarDetailScrollbar" :options="nativeScrolling ? false : {suppressScrollX: true}">
         
            <div class="sidebar-detail__header sidebar__section sidebar__section--border-bottom ui-center-content">
                <div class="sidebar__reset sidebar__action-icon sidebar-detail__header__back" @click="closeClickHandler">
                   <SmrTooltip :content="tooltipContent ? tooltipContent : ''" :position="tooltipContent ? 'bottom-start': ''">
                        <icon><IconChevronLeft /></icon>
                   </SmrTooltip>
                </div>
                <span class="sidebar__title bold-label">{{ title }}</span>
            </div>
            
            <POIDetail v-if="activeDetailType === 'poi'" ref="poiDetail"/>
            <RouteDetail v-if="activeDetailType === 'route'"/>
            <SavedRoutes v-if="activeDetailType === 'saved-routes'"/>
            <FavouritePicker v-if="activeDetailType === 'favourite-list'"/>

            <StickyHint v-if="activeDetailType === 'route'" :activeDetailType="activeDetailType" />
        </component>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Icon from '@/components/Icon.vue'
import IconChevronLeft from '@/components/icons/IconChevronLeft.vue'
import eventBus from '@/utils/event-bus';
import { getPOIDetailFromUrl } from '@/utils/url-utils'
import SmrTooltip from '@/components/SmrTooltip.vue'
import StickyHint from '@/components/StickyHint.vue'


export default {
    components: {
        POIDetail: () => import(/* webpackChunkName: "POIDetail" */ '@/components/POIDetail.vue'),
        RouteDetail: () => import(/* webpackChunkName: "RouteDetail" */ '@/components/RouteDetail.vue'),
        SavedRoutes: () => import(/* webpackChunkName: "SavedRoutes" */ '@/components/SavedRoutes.vue'),
        FavouritePicker: () => import(/* webpackChunkName: "FavouritePicker" */ '@/components/FavouritePicker.vue'),
        Icon,
        IconChevronLeft,
        SmrTooltip,
        StickyHint,
    },
    watch: {
        activePoiId() {
            this.checkIfSidebarDetailOpen();
        },
        detailRouteId() {
            this.checkIfSidebarDetailOpen();
        },
        savedRoutesListOpen() {
            this.checkIfSidebarDetailOpen();
        },
        favouritePickerOpen() {
            this.checkIfSidebarDetailOpen();
        },
        activeDetailType(newVal) {
            if(newVal) {
                eventBus.$emit('hideScrollbar');
            } else {
                eventBus.$emit('showScrollbar');
            }
        }
    },
    computed: {
        ...mapGetters(['nativeScrolling', 'activePoiId', 'poiToRemoveFromMap', 'detailRouteId', 'activeRouteMode', 'savedRoutesListOpen', 'sidebarDetailOpen', 'favouritePickerOpen', 'selectedPOIs', 'mapFullyLoaded', 'mobile', 'customActivePoiType', 'previousActivePoi']),
        title() {
            let title = false;
            switch(this.activeDetailType) {
                case 'poi':
                    title = 'Ausgewählter Ort';
                break;
                case 'route':
                    title = this.activeRouteMode === 'PUBLIC' ? 'Verbindungsdetails' : 'Routendetails';
                break;
                case 'saved-routes':
                    title = 'Gespeicherte Routen';
                break;
                case 'favourite-list':
                    title = 'Aus Favoriten wählen';
                break;
            }
            return title;
        },
        tooltipContent() {
            let content = false;
            switch(this.activeDetailType) {
                case 'poi':
                     content = 'Zurück zur Liste';
                break;
                case 'route':
                    content = 'Zurück zur Routenübersicht';
                break;
                case 'saved-routes':
                     content = 'Zurück zur Startszeite';
                break;
                case 'favourite-list':
                    content = false;
                break;
            }
            return content;
        },
        activeDetailType() {
            if(this.activePoiId) {
                return 'poi';
            } else if (this.detailRouteId) {
                return 'route';
            } else if (this.savedRoutesListOpen) {
                return 'saved-routes';
            } else if (this.favouritePickerOpen) {
                return 'favourite-list'
            }
            return false;
        },
    },
    created() {
        this.addListeners()
    },
    beforeDestroy() {
        this.removeListeners()
    },
    methods: {
        closeClickHandler() {
            eventBus.$emit('itemClosed');
            this.closeSidebarDetail()
        },
        closeSidebarDetail() {
            eventBus.$emit('showScrollbar');
            this.$store.commit('setMapTrafficFromUrl', false);
            switch(this.activeDetailType) {
                case 'poi':
                    if (this.previousActivePoi) {
                        this.$store.commit('setActivePoiId', this.previousActivePoi.id);
                        this.$store.commit('setCustomActivePoiType', this.previousActivePoi.type);
                        this.$store.commit('setCustomActivePoiSubType', this.previousActivePoi.subType);
                        this.$store.commit('setPreviousActivePoi', false)
                    } else {
                        this.$store.commit('setActivePoiId', false);
                    }
                break;
                case 'route':
                    this.$store.dispatch('setDetailRoute', {id: false});
                break;
                case 'saved-routes':
                    this.$store.commit('setSavedRoutesListOpen', false);
                break;
                case 'favourite-list':
                    this.$store.commit('setFavouritePickerOpen', false);
                break;
            }
        },
        showPOIDetailHandler(data) {
            this.showPOIDetail(data)
            this.$store.commit('setPoiDetailFromUrl', true);
            if(data.type === 'traffic') {
                this.$store.commit('setMapTrafficFromUrl', true);
            }
        },
        urlChangedHandler(e) {
            if(!this.mapFullyLoaded) return

            if(e.to.query.poidetail !== undefined && e.to.query.poidetail !== false) {
                // if a poi detail is opened from route URL do nothing
                return
            } else if(e.to.name !== 'POI Detail') {
                if(this.activePoiId) {
                    this.hidePOIDetail()
                }
            } else if(e.to.name === 'POI Detail') {
                const poiDetails = getPOIDetailFromUrl(e.to)
                if(this.activePoiId === poiDetails.id) return
                if(this.selectedPOIs.length && this.selectedPOIs.includes(poiDetails.type)) {
                    this.$store.commit('setCustomActivePoiType', poiDetails.type);
                    this.$store.commit('setActivePoiId', poiDetails.id);
                    this.$store.commit('setCustomActivePoiSubType', poiDetails.subType);
                } else {
                    this.showPOIDetail(poiDetails)
                }
            }
        },
        collapsedHandler(config) {
            if(config === undefined) return false
            const scrollbarEl = this.nativeScrolling ? this.$refs.sidebarDetailScrollbar : this.$refs.sidebarDetailScrollbar.$el;
            if(!config.expanded) {
                // const topOffset = 0;
                const topOffset = this.mobile ? this.$refs.poiDetail.$el.children[1].children[1].innerHeight : 0;
                scrollbarEl.scroll({top: topOffset, behavior: "smooth"})
            } else {
                // const topOffset = this.mobile ? config.target + 700 : config.target;
                const topOffset = this.mobile ?  this.$refs.poiDetail.$el.children[1].children[1].scrollHeight + config.target : config.target;
                setTimeout(function(){ scrollbarEl.scroll({top: topOffset, behavior: "smooth"}) }, 300);
            }
        },
        triggerPOIDetailScrollbar() {
            const self = this;
            setTimeout(function(){ 
                if(self.$refs.sidebarDetailScrollbar && !self.mobile) { 
                    self.$refs.sidebarDetailScrollbar.update();
                }
            }, 1000)
        },
        scrollPOIDetailToBottomHandler(e) {
            const scrollbarEl = this.nativeScrolling ? this.$refs.sidebarDetailScrollbar : this.$refs.sidebarDetailScrollbar.$el;
            const topOffset = e.offset ? scrollbarEl.scrollTop - e.offset : scrollbarEl.scrollHeight
            scrollbarEl.scrollTop = topOffset
        },
        addListeners() {
            eventBus.$on('showPOIDetail', this.showPOIDetailHandler)
            eventBus.$on('triggerPOIDetailScrollbar', this.triggerPOIDetailScrollbar)
            eventBus.$on('urlChanged', this.urlChangedHandler)
            eventBus.$on('collapsed', this.collapsedHandler)
            eventBus.$on('scrollPOIDetailToBottom', this.scrollPOIDetailToBottomHandler)
        },
        removeListeners() {
            eventBus.$off('showPOIDetail', this.showPOIDetailHandler)
            eventBus.$off('triggerPOIDetailScrollbar', this.triggerPOIDetailScrollbar)
            eventBus.$off('urlChanged', this.urlChangedHandler)
            eventBus.$off('collapsed', this.collapsedHandler)
            eventBus.$off('scrollPOIDetailToBottom', this.scrollPOIDetailToBottomHandler)
        },
        checkIfSidebarDetailOpen() {
            const open = this.activePoiId || this.detailRouteId || this.savedRoutesListOpen || this.favouritePickerOpen;
            this.$store.commit('setSidebarDetailOpen', open);
        },
        showPOIDetail(data) {
            this.$store.commit('setCustomActivePoiType', data.type);
            this.$store.commit('setActivePoiId', data.id);
            this.$store.commit('setCustomActivePoiSubType', data.subType);
        },
        hidePOIDetail() {
            this.$store.commit('setCustomActivePoiType', false);
            this.$store.commit('setActivePoiId', false);
            this.$store.commit('setCustomActivePoiSubType', false);
            this.$store.commit('setMapTrafficFromUrl', false);
        }
    }
}
</script>

<style lang="scss">
.sidebar-detail {
    $block: &;
    background: white;
    z-index: 5;
    &__header {
        padding: 18px 35px !important;
        position: relative;

        &__back {
            position: absolute;
            top: 50%;
            left: 16px;
            transform: translateY(-50%);
        }
    }
    &__scrollbar {
        display: flex;
        flex-direction: column;
        height: 100%;
        .ps__rail-y {
            z-index: 100;
        }
    }
    &__section {
        margin-bottom: 32px;
    }
    &__subtitle {
        margin-bottom: 12px;
        display: flex;
        align-items: center;

        &--space-top {
            margin-top: 24px;
        }

        &__loader {
            margin-left: 6px;
            margin-top: -8px;
            margin-bottom: -8px;
        }
    }

}
</style>